import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ErrorMessage, PrimaryButton, Stepper } from "widgets";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import {
  useSelectedOrderDetails,
  useVerticalAndTenantsId,
} from "store/backend/assets";
import {
  useProcessPreference,
  usePreferenceSuggestions,
  useSuggestedPrograms,
  useSkipBatching,
} from "store/backend/batchPreferenceApi";
import {
  BATCH_FLOW_STEP_INDEX,
  BATCH_FLOW_STEPS,
  PROGRAM_STATUS,
} from "constants/batchPreference";
import routes from "constants/routes";
import {
  areAllProgramsResolved,
  getGatewayBranchId,
  getGatewayTenantId,
  getNextPendingProgramId,
  hasExistingPaymentActivity,
  isOrderEligibleForBatchPreference,
  normalizeUamId,
} from "utils/batchPreferenceUtils";
import {
  clearCompletedBatchDecisions,
  clearIncompleteBatchDecisions,
  grantBatchPreferenceAccess,
  markPaymentDetailsEntered,
  saveCompletedBatchDecisions,
} from "utils/batchPreferenceStorage";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import BatchPreferencesSummary from "components/BatchPreference/BatchPreferencesSummary";
import ConfirmBatchModal from "components/BatchPreference/ConfirmBatchModal";
import ProceedToPaymentModal from "components/BatchPreference/ProceedToPaymentModal";
import ProgramBatchTab from "components/BatchPreference/ProgramBatchTab";
import SkipBatchModal from "components/BatchPreference/SkipBatchModal";

import styles from "components/BatchPreference/BatchPreference.module.scss";

const goToPayment = (navigate, orderId) => {
  markPaymentDetailsEntered(orderId);
  navigate(`${routes.paymentPreference.paymentPreference}?order_id=${orderId}`);
};

const BatchPreference = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const orderId = searchParams.get("order_id");
  const uamIdFromQuery = searchParams.get("uam_id");

  const [programs, setPrograms] = useState([]);
  const [activeProgramId, setActiveProgramId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isSkipOpen, setIsSkipOpen] = useState(false);
  const [isProceedModalOpen, setIsProceedModalOpen] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);

  const {
    isPending: isOrderLoading,
    data: order,
    isError: isOrderError,
    error: orderError,
  } = useSelectedOrderDetails(orderId);

  const userId =
    normalizeUamId(uamIdFromQuery) || normalizeUamId(order?.customer_id);
  const { data: verticalAndTenants, isPending: isVerticalsLoading } =
    useVerticalAndTenantsId();
  const isEligible = isOrderEligibleForBatchPreference(order);

  const activeProgram =
    programs.find((program) => program.id === activeProgramId) || programs[0];
  const gatewayTenantId = useMemo(
    () => getGatewayTenantId(verticalAndTenants, order),
    [verticalAndTenants, order],
  );
  const gatewayBranchId = useMemo(
    () => getGatewayBranchId(verticalAndTenants, order),
    [verticalAndTenants, order],
  );

  const {
    isLoading: isProgramsLoading,
    data: fetchedPrograms,
    isError: isProgramsError,
    error: programsError,
  } = useSuggestedPrograms({
    orderId,
    order,
    userId,
    gatewayTenantId,
    gatewayBranchId,
    enabled: Boolean(order && isEligible && userId),
  });

  const {
    data: schedules = [],
    isLoading: isSchedulesLoading,
    isError: isSchedulesError,
    error: schedulesError,
  } = usePreferenceSuggestions({
    programId: activeProgram?.id,
    order,
    userId,
    gatewayTenantId,
    gatewayBranchId,
    enabled: Boolean(
      activeProgram &&
        activeProgram.status === PROGRAM_STATUS.PENDING &&
        !showSummary,
    ),
  });

  const { mutate: processPreference, isPending: isProcessPending } =
    useProcessPreference();
  const { mutate: skipBatching, isPending: isSkipPending } = useSkipBatching();

  useEffect(() => {
    if (!orderId || !order || hasHydrated) return;

    if (hasExistingPaymentActivity(order)) {
      setHasHydrated(true);
      navigate(
        `${routes.paymentPreference.paymentPreference}?order_id=${orderId}`,
        { replace: true },
      );
      return;
    }

    grantBatchPreferenceAccess(orderId);

    if (!isEligible) {
      setHasHydrated(true);
      return;
    }

    clearIncompleteBatchDecisions(orderId);
  }, [
    order,
    orderId,
    isEligible,
    hasHydrated,
    navigate,
  ]);

  useEffect(() => {
    if (hasHydrated || !fetchedPrograms) return;
    if (hasExistingPaymentActivity(order)) {
      return;
    }
    setPrograms(fetchedPrograms);
    setActiveProgramId(fetchedPrograms[0]?.id || null);
    setShowSummary(false);
    setHasHydrated(true);
  }, [fetchedPrograms, hasHydrated, order]);

  const applyDecision = (programId, nextStatus, decision) => {
    const updated = programs.map((program) =>
      program.id === programId
        ? { ...program, status: nextStatus, decision }
        : program,
    );
    setPrograms(updated);
    setSelectedBatch(null);
    setIsSkipOpen(false);

    if (areAllProgramsResolved(updated)) {
      saveCompletedBatchDecisions(orderId, updated);
      setShowSummary(true);
      return;
    }

    const nextId = getNextPendingProgramId(updated, programId);
    setActiveProgramId(nextId);
    setShowSummary(false);
  };

  const handleConfirmBatch = (batch, startDate) => {
    processPreference(
      {
        programId: activeProgram.id,
        decision: {
          status: PROGRAM_STATUS.CONFIRMED,
          schedule: batch.schedule,
          schedules: batch.schedules,
          startDate,
        },
        order,
        userId,
        gatewayTenantId,
        gatewayBranchId,
      },
      {
        onSuccess: () => {
          applyDecision(activeProgram.id, PROGRAM_STATUS.CONFIRMED, {
            batchLabel: batch.label,
            schedule: batch.schedule,
            schedules: batch.schedules,
            startDate,
            batchId: null,
          });
        },
        onError: (error) => {
          openSnackbar(
            error?.message || "Unable to save the confirmed batch.",
            "error",
          );
        },
      },
    );
  };

  const handleConfirmSkip = ({ skipReasonCode, skipReasonDetail }) => {
    skipBatching(
      {
        orderId,
        programId: activeProgram.id,
        skipReasonCode,
        skipReasonDetail,
      },
      {
        onSuccess: () => {
          applyDecision(activeProgram.id, PROGRAM_STATUS.SKIPPED, {
            skipReasonCode,
            skipReasonDetail,
          });
        },
        onError: (error) => {
          openSnackbar(
            error?.message || "Unable to save the skip reason.",
            "error",
          );
        },
      },
    );
  };

  const handleEdit = () => {
    const updated = programs.map((program) => ({
      ...program,
      status: PROGRAM_STATUS.PENDING,
      decision: null,
    }));
    clearCompletedBatchDecisions(orderId);
    grantBatchPreferenceAccess(orderId);
    setPrograms(updated);
    setActiveProgramId(updated[0]?.id || null);
    setShowSummary(false);
    setIsProceedModalOpen(false);
  };

  const handleProceedClick = () => {
    setIsProceedModalOpen(true);
  };

  const handleConfirmProceed = () => {
    setIsProceedModalOpen(false);
    goToPayment(navigate, orderId);
  };

  if (!orderId) {
    return <ErrorMessage>Order is required to select a batch.</ErrorMessage>;
  }

  if (
    isOrderLoading ||
    (isEligible && isVerticalsLoading) ||
    (isEligible && isProgramsLoading && !hasHydrated) ||
    (order && hasExistingPaymentActivity(order))
  ) {
    return <FullPageLoader />;
  }

  if (isOrderError) {
    return (
      <ErrorMessage>
        {orderError?.message || "Unable to load order details."}
      </ErrorMessage>
    );
  }

  if (!isEligible) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <h2 className={styles.pageTitle}>No batch selection required</h2>
          <p>This order has no Programs that need a batching decision.</p>
          <div className={styles.emptyActions}>
            <PrimaryButton onClick={() => goToPayment(navigate, orderId)}>
              Proceed to Payment
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  if (isProgramsError) {
    return (
      <div className={styles.page}>
        <ErrorMessage>
          {programsError?.message || "Unable to load eligible Programs."}
        </ErrorMessage>
      </div>
    );
  }

  if (hasHydrated && programs.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <h2 className={styles.pageTitle}>No batch selection required</h2>
          <p>No eligible Programs were returned for this order.</p>
          <div className={styles.emptyActions}>
            <PrimaryButton onClick={() => goToPayment(navigate, orderId)}>
              Proceed to Payment
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Stepper
        activeStep={
          showSummary
            ? BATCH_FLOW_STEP_INDEX.REVIEW
            : BATCH_FLOW_STEP_INDEX.SELECT_PROGRAM
        }
      >
        <Stepper.Steps
          steps={BATCH_FLOW_STEPS}
          activeStep={
            showSummary
              ? BATCH_FLOW_STEP_INDEX.REVIEW
              : BATCH_FLOW_STEP_INDEX.SELECT_PROGRAM
          }
        />
      </Stepper>

      {showSummary ? (
        <BatchPreferencesSummary
          programs={programs}
          onEdit={handleEdit}
          onProceed={handleProceedClick}
        />
      ) : (
        <>
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Select Program and Batch</h2>
            <p className={styles.pageSubtitle}>
              Choose a program first, then select your preferred batch
            </p>
          </div>
          <div className={styles.programRow}>
            <div className={styles.tabs}>
              {programs.map((program) => {
                return (
                  <button
                    key={program.id}
                    type="button"
                    className={`${styles.tab} ${
                      program.id === activeProgramId ? styles.tabActive : ""
                    } ${
                      program.status !== PROGRAM_STATUS.PENDING
                        ? styles.tabResolved
                        : ""
                    }`}
                    onClick={() => {
                      if (program.status === PROGRAM_STATUS.PENDING) {
                        setActiveProgramId(program.id);
                      }
                    }}
                  >
                    {program.name}
                    {program.status !== PROGRAM_STATUS.PENDING
                      ? ` · ${program.status === PROGRAM_STATUS.CONFIRMED ? "Confirmed" : "Skipped"}`
                      : ""}
                  </button>
                );
              })}
            </div>
            {activeProgram?.status === PROGRAM_STATUS.PENDING ? (
              <button
                type="button"
                className={styles.skipButton}
                onClick={() => setIsSkipOpen(true)}
              >
                Skip Batching
              </button>
            ) : null}
          </div>
          {activeProgram ? (
            <ProgramBatchTab
              program={activeProgram}
              schedules={schedules}
              isLoading={
                isSchedulesLoading ||
                (isVerticalsLoading && !gatewayTenantId)
              }
              isError={
                isSchedulesError ||
                Boolean(
                  activeProgram.status === PROGRAM_STATUS.PENDING &&
                    ((!isVerticalsLoading && !gatewayTenantId) ||
                      (gatewayTenantId && !userId)),
                )
              }
              error={
                schedulesError ||
                (!gatewayTenantId
                  ? new Error(
                      "Unable to resolve gateway tenant id for recommended schedules.",
                    )
                  : !userId
                    ? new Error(
                        "Unable to load schedules because the user ID is missing.",
                      )
                    : null)
              }
              onSelectBatch={setSelectedBatch}
            />
          ) : null}
        </>
      )}

      <ConfirmBatchModal
        open={Boolean(selectedBatch)}
        programName={activeProgram?.name}
        batch={selectedBatch}
        isPending={isProcessPending}
        onCancel={() => setSelectedBatch(null)}
        onConfirm={handleConfirmBatch}
      />
      <SkipBatchModal
        open={isSkipOpen}
        programName={activeProgram?.name}
        isPending={isSkipPending}
        onCancel={() => setIsSkipOpen(false)}
        onConfirm={handleConfirmSkip}
      />
      <ProceedToPaymentModal
        open={isProceedModalOpen}
        onCancel={() => setIsProceedModalOpen(false)}
        onConfirm={handleConfirmProceed}
      />
    </div>
  );
};

export default BatchPreference;
